/** Class labels used across teacher / admin forms (NUR through class 8). */
export const CLASSES = ['NUR', 'L.K.G', 'UKG', '1', '2', '3', '4', '5', '6', '7', '8'];

/** Map CSV / legacy class labels to canonical CLASSES values. */
const CLASS_ALIASES = {
  nur: 'NUR',
  lkg: 'L.K.G',
  'l.k.g': 'L.K.G',
  ukg: 'UKG',
  'u.k.g': 'UKG',
  i: '1',
  ii: '2',
  iii: '3',
  iv: '4',
  v: '5',
  vi: '6',
  vii: '7',
  viii: '8',
  '1': '1',
  '2': '2',
  '3': '3',
  '4': '4',
  '5': '5',
  '6': '6',
  '7': '7',
  '8': '8',
};

/** Normalize school CSV class (e.g. VIII, L.K.G) to Nur–8 picker values. */
export function normalizeSchoolClass(raw) {
  const s = String(raw ?? '').trim();
  if (!s) return '';
  if (CLASSES.includes(s)) return s;
  if (s === 'LKG') return 'L.K.G';
  if (s === 'Nur') return 'NUR';
  const key = s.toLowerCase().replace(/\s+/g, '');
  if (CLASS_ALIASES[key]) return CLASS_ALIASES[key];
  const noDots = key.replace(/\./g, '');
  if (CLASS_ALIASES[noDots]) return CLASS_ALIASES[noDots];
  return '';
}

/** Display label for UI (L.K.G already correct). */
export function formatClassLabel(className) {
  const c = String(className ?? '').trim();
  if (c === 'LKG') return 'L.K.G';
  if (c === 'Nur') return 'NUR';
  return c || '—';
}

/** Match legacy Firestore values when querying students by class. */
export function classQueryKeys(className) {
  const c = normalizeSchoolClass(className) || String(className || '').trim();
  if (c === 'L.K.G') return ['L.K.G', 'LKG'];
  if (c === 'NUR') return ['NUR', 'Nur'];
  return [c];
}

export function classOptions() {
  return CLASSES.map((c) => ({ value: c, label: formatClassLabel(c) }));
}

export function classOptionsWithEmpty(emptyLabel = 'Select class (NUR–8)…') {
  return [{ value: '', label: emptyLabel }, ...classOptions()];
}

/** Class picker including “All classes” (bulk exam/test creation). */
export const ALL_CLASSES_VALUE = '__ALL__';

export function classOptionsWithAll() {
  return [{ value: ALL_CLASSES_VALUE, label: 'All classes' }, ...classOptions()];
}

/** Classes a teacher may access (all classes if allClasses flag). */
export function teacherClassOptions(profile) {
  const all = classOptions();
  if (!profile || profile.role === 'SUPERUSER' || profile.allClasses) return all;
  const assigned = Array.isArray(profile.assignedClasses) ? profile.assignedClasses : [];
  if (!assigned.length) return all;
  const set = new Set(assigned.map((c) => normalizeSchoolClass(c) || c));
  return all.filter((o) => set.has(o.value));
}

export function teacherCanAccessClass(profile, className) {
  const c = normalizeSchoolClass(className) || className;
  if (!profile || profile.role === 'SUPERUSER' || profile.allClasses) return true;
  const assigned = (profile.assignedClasses || []).map((x) => normalizeSchoolClass(x) || x);
  return assigned.length === 0 || assigned.includes(c);
}
